import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslate } from 'react-admin';
import LocaleTabsField from 'src/components/inputs/LocaleTabsField';

const LOCALES = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
];

type EditMarketQuestionDialogProps = {
  open: boolean;
  type?: string;
  initialQuestion: string;
  initialQuestionZh: string;
  originalQuestion: string;
  originalQuestionZh: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (question: string, questionZh: string) => void;
};

export default function EditMarketQuestionDialog({
  open,
  type,
  initialQuestion,
  initialQuestionZh,
  originalQuestion,
  originalQuestionZh,
  loading,
  onClose,
  onConfirm,
}: EditMarketQuestionDialogProps) {
  const translate = useTranslate();
  const isEvent = type === 'EVENT';
  const [question, setQuestion] = useState(initialQuestion);
  const [questionZh, setQuestionZh] = useState(initialQuestionZh);

  useEffect(() => {
    if (open) {
      setQuestion(initialQuestion);
      setQuestionZh(initialQuestionZh);
    }
  }, [open, initialQuestion, initialQuestionZh]);

  const handleConfirm = () => onConfirm(question, questionZh);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleConfirm();
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {translate(
          isEvent ? 'module.featuredMarkets.editEvent' : 'module.featuredMarkets.editMarket'
        )}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <LocaleTabsField
            locales={LOCALES}
            value={{ zh: questionZh, en: question }}
            onChange={(v) => {
              setQuestion(v.en ?? '');
              setQuestionZh(v.zh ?? '');
            }}
            placeholder={{ en: originalQuestion, zh: originalQuestionZh }}
            autoFocus
            label={translate(
              isEvent
                ? 'module.featuredMarkets.customTitle'
                : 'module.featuredMarkets.customQuestion'
            )}
            onKeyDown={handleKeyDown}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {translate('ra.action.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={loading}
          disabled={question === initialQuestion && questionZh === initialQuestionZh}
          onClick={handleConfirm}
        >
          {translate('ra.action.save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
